import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { searchPublicPlayers } from "../../services/playerService";
import { getHighlights } from "../../services/highlightsService";
import { addFavorite, removeFavorite } from "../../services/favoritesService";
import { supabase } from "../../supabase/supabaseClient";
import {
    acceptConnectionRequest,
    getConnectionsForCurrentUser,
    rejectConnectionRequest,
    sendConnectionRequest
} from "../../services/connectionsService";
import { followUser, getFollowingUserIds, unfollowUser } from "../../services/followsService";
import { POSITION_OPTIONS } from "../../constants/positions";
import "./Explore.css";

function Explore() {
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const initialQ = urlParams.get("q") || "";

    // Filter states
    const [selectedPositions, setSelectedPositions] = useState([]);
    const [minAge, setMinAge] = useState(8);
    const [maxAge, setMaxAge] = useState(35);
    const [minHeight, setMinHeight] = useState(140);
    const [maxHeight, setMaxHeight] = useState(220);
    const [minWeight, setMinWeight] = useState(40);
    const [maxWeight, setMaxWeight] = useState(120);
    const [city, setCity] = useState("");
    // const [team, setTeam] = useState("");
    const [q, setQ] = useState(initialQ);
    const [footedness, setFootedness] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [authedUser, setAuthedUser] = useState(null);
    const [connectionsByUserId, setConnectionsByUserId] = useState({});
    const [connectionsStats, setConnectionsStats] = useState({
        accepted: 0,
        pendingInbound: 0,
        pendingOutbound: 0
    });
    const [followsByUserId, setFollowsByUserId] = useState({});
    const [connectionActionId, setConnectionActionId] = useState(null);
    const [followActionId, setFollowActionId] = useState(null);
    const [viewMode, setViewMode] = useState("grid");
    const [userRole, setUserRole] = useState(null); // 'scout', 'coach', 'player', or null
    const [highlightsByPlayerId, setHighlightsByPlayerId] = useState({}); // { playerId: [highlights] }
    const [selectedHighlight, setSelectedHighlight] = useState(null); // { url, type, playerName }
    const [favoritedByPlayerId, setFavoritedByPlayerId] = useState({}); // { playerId: true/false }
    const authedUserId = authedUser?.id || null;
    const resultsPerPage = 6;

    const refreshConnections = async (user) => {
        if (!user) {
            setConnectionsByUserId({});
            setConnectionsStats({ accepted: 0, pendingInbound: 0, pendingOutbound: 0 });
            return;
        }

        try {
            const data = await getConnectionsForCurrentUser();
            const byUserId = {};
            let accepted = 0;
            let pendingInbound = 0;
            let pendingOutbound = 0;

            (data || []).forEach((connection) => {
                const isRequester = connection.requester_id === user.id;
                const otherId = isRequester ? connection.addressee_id : connection.requester_id;
                byUserId[otherId] = connection;

                if (connection.status === "accepted") {
                    accepted += 1;
                } else if (connection.status === "pending") {
                    if (isRequester) {
                        pendingOutbound += 1;
                    } else {
                        pendingInbound += 1;
                    }
                }
            });

            setConnectionsByUserId(byUserId);
            setConnectionsStats({ accepted, pendingInbound, pendingOutbound });
        } catch (err) {
            console.error("Error loading connections:", err);
        }
    };

    useEffect(() => {
        let isMounted = true;

        const loadSession = async () => {
            const { data } = await supabase.auth.getSession();
            if (!isMounted) return;
            setAuthedUser(data?.session?.user ?? null);

            // Detect user role - default to "player" if not found
            if (data?.session?.user) {
                const user = data.session.user;
                try {
                    const { data: upData } = await supabase
                        .from("user_profiles")
                        .select("role")
                        .eq("user_id", user.id)
                        .maybeSingle();
                    const role = (upData?.role || user?.user_metadata?.role || "player").toLowerCase();
                    if (!isMounted) return;
                    setUserRole(role);
                } catch (err) {
                    console.error("Error fetching user role:", err);
                    if (!isMounted) return;
                    setUserRole(user?.user_metadata?.role?.toLowerCase() || "player");
                }
            } else {
                if (!isMounted) return;
                setUserRole(null);
            }
        };

        loadSession();

        const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
            if (!isMounted) return;
            setAuthedUser(session?.user ?? null);
            if (session?.user) {
                const user = session.user;
                supabase
                    .from("user_profiles")
                    .select("role")
                    .eq("user_id", user.id)
                    .maybeSingle()
                    .then(({ data: upData }) => {
                        const role = (upData?.role || user?.user_metadata?.role || "player").toLowerCase();
                        if (isMounted) setUserRole(role);
                    })
                    .catch((err) => {
                        console.error("Error fetching user role:", err);
                        if (isMounted) setUserRole(user?.user_metadata?.role?.toLowerCase() || "player");
                    });
            } else {
                if (isMounted) setUserRole(null);
            }
        });

        return () => {
            isMounted = false;
            authListener?.subscription?.unsubscribe();
        };
    }, []);

    // Load players on mount and when filters change (debounced)
    useEffect(() => {
        const loadPlayers = async () => {
            setLoading(true);
            setError("");

            const { data, error: searchError } = await searchPublicPlayers({
                positions: selectedPositions,
                minAge: minAge !== 8 ? minAge : null,
                maxAge: maxAge !== 35 ? maxAge : null,
                minHeight: minHeight !== 140 ? minHeight : null,
                maxHeight: maxHeight !== 220 ? maxHeight : null,
                minWeight: minWeight !== 40 ? minWeight : null,
                maxWeight: maxWeight !== 120 ? maxWeight : null,
                city: city || null,
                // team: team || null,
                q: q || null,
                footedness: footedness || null,
            });

            if (searchError) {
                setError("Failed to load players");
                setPlayers([]);
                setHighlightsByPlayerId({});
                setFavoritedByPlayerId({});
            } else {
                setPlayers(data || []);
                setCurrentPage(1);
                
                // Fetch highlights and favorites for coaches/scouts only
                if ((userRole === "scout" || userRole === "coach") && authedUserId) {
                    const newHighlights = {};
                    const newFavorited = {};
                    
                    const highlightPromises = (data || []).map(async (player) => {
                        const { data: hlData } = await getHighlights(player.user_id);
                        if (hlData && hlData.length > 0) {
                            newHighlights[player.id] = hlData;
                        }
                    });
                    
                    // Check which players are already favorited
                    const favPromises = (data || []).map(async (player) => {
                        const { data: favCheck } = await supabase
                            .from("favorites")
                            .select("id")
                            .eq("scout_id", authedUserId)
                            .eq("player_id", player.id)
                            .maybeSingle();
                        if (favCheck) {
                            newFavorited[player.id] = true;
                        }
                    });
                    
                    Promise.all([...highlightPromises, ...favPromises]).then(() => {
                        setHighlightsByPlayerId(newHighlights);
                        setFavoritedByPlayerId(newFavorited);
                    });
                } else {
                    setHighlightsByPlayerId({});
                    setFavoritedByPlayerId({});
                }
            }

            setLoading(false);
        };

        // Debounce: wait 300ms after user stops adjusting before searching
        const timeoutId = setTimeout(() => {
            loadPlayers();
        }, 300);

        // Cleanup: cancel the timeout if filters change again before 300ms
        return () => clearTimeout(timeoutId);  
    }, [selectedPositions, minAge, maxAge, minHeight, maxHeight, minWeight, maxWeight, city, q, footedness, userRole, authedUserId]);

    // Keep q in sync when URL changes (e.g., via NavBar search)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        setQ(params.get("q") || "");
    }, [location.search]);

    useEffect(() => {
        refreshConnections(authedUser);
    }, [authedUser]);

    useEffect(() => {
        const refreshFollows = async () => {
            if (!authedUser?.id) {
                setFollowsByUserId({});
                return;
            }

            try {
                const followedIds = await getFollowingUserIds();
                const next = {};
                (followedIds || []).forEach((userId) => {
                    if (userId) next[userId] = true;
                });
                setFollowsByUserId(next);
            } catch (error) {
                console.error("Error loading follow relationships:", error);
                setFollowsByUserId({});
            }
        };

        refreshFollows();
    }, [authedUser?.id]);

    const togglePosition = (pos) => {
        setSelectedPositions((prev) =>
            prev.includes(pos) ? prev.filter((p) => p !== pos) : [...prev, pos]
        );
    };

    const clearFilters = () => {
        setSelectedPositions([]);
        setMinAge(8);
        setMaxAge(35);
        setMinHeight(140);
        setMaxHeight(220);
        setMinWeight(40);
        setMaxWeight(120);
        setCity("");
        // setTeam("");
        setFootedness("");
    };

    const getAge = (dob) => {
        if (!dob) return null;
        const birth = new Date(dob);
        if (Number.isNaN(birth.getTime())) return null;
        const today = new Date();
        return Math.floor((today - birth) / (365.25 * 24 * 60 * 60 * 1000));
    };

    const totalPages = Math.ceil(players.length / resultsPerPage);
    const startIdx = (currentPage - 1) * resultsPerPage;
    const endIdx = startIdx + resultsPerPage;
    const paginatedPlayers = players.slice(startIdx, endIdx);

		return (
			<main className="page explore-page">
                <div className="explore-shell">
                    <div className="explore-layout">
                        <aside className="explore-left">
                            <div className="explore-card explore-manage-card">
                                <h3>{userRole === "player" ? "Manage your network" : "Seeking Database"}</h3>
                                <div className="explore-manage-list">
                                    {userRole === "player" ? (
                                        <>
                                            <Link to="/connections" className="explore-manage-row explore-manage-row-link">
                                                <span className="explore-connections-link-label">Connections</span>
                                                <strong className="explore-connections-link-count">{connectionsStats.accepted}</strong>
                                            </Link>
                                            <div className="explore-manage-row">
                                                <span>Pending requests</span>
                                                <strong>{connectionsStats.pendingInbound}</strong>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="explore-manage-row">
                                            <p style={{ fontSize: "0.85rem", color: "rgba(255, 255, 255, 0.7)", margin: 0 }}>
                                                Browse players and add them to your database by favoriting them and adding notes.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="explore-card explore-filters">
                                <div className="filters-header">
                                    <h2>Filters</h2>
                                    {(selectedPositions.length > 0 || minAge !== 8 || maxAge !== 35 || minHeight !== 140 || maxHeight !== 220 || minWeight !== 40 || maxWeight !== 120 || city || footedness) && (
                                        <button className="btn-clear-filters" onClick={clearFilters}>
                                            Clear All
                                        </button>
                                    )}
                                </div>

                                <div className="filter-group">
                                    <label className="filter-label">Positions</label>
                                    <div className="positions-filter">
                                        {POSITION_OPTIONS.map(({ value, short, label }) => (
                                            <button
                                                key={value}
                                                type="button"
                                                className={`position-filter-btn ${selectedPositions.includes(value) ? "selected" : ""}`}
                                                onClick={() => togglePosition(value)}
                                                title={label}
                                            >
                                                {short}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="filter-group">
                                    <label className="filter-label">
                                        Age: {minAge} - {maxAge} yrs
                                    </label>
                                    <div className="range-slider">
                                        <input
                                            type="range"
                                            className="range-input range-input-min"
                                            min="8"
                                            max="35"
                                            value={minAge}
                                            onChange={(e) => {
                                                const val = parseInt(e.target.value);
                                                if (val <= maxAge) setMinAge(val);
                                            }}
                                        />
                                        <input
                                            type="range"
                                            className="range-input range-input-max"
                                            min="8"
                                            max="35"
                                            value={maxAge}
                                            onChange={(e) => {
                                                const val = parseInt(e.target.value);
                                                if (val >= minAge) setMaxAge(val);
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="filter-group">
                                    <label className="filter-label">City</label>
                                    <input
                                        type="text"
                                        placeholder="e.g., Toronto"
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                        className="filter-input"
                                    />
                                </div>

                                <div className="filter-group">
                                    <label className="filter-label">Footedness</label>
                                    <div className="footedness-filter">
                                        {["Left", "Right", "Both"].map((f) => (
                                            <button
                                                key={f}
                                                type="button"
                                                className={`position-filter-btn ${footedness === f ? "selected" : ""}`}
                                                onClick={() => setFootedness(footedness === f ? "" : f)}
                                            >
                                                {f}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="filter-group">
                                    <label className="filter-label">
                                        Height: {minHeight} - {maxHeight} cm
                                    </label>
                                    <div className="range-slider">
                                        <input
                                            type="range"
                                            className="range-input range-input-min"
                                            min="140"
                                            max="220"
                                            value={minHeight}
                                            onChange={(e) => {
                                                const val = parseInt(e.target.value);
                                                if (val <= maxHeight) setMinHeight(val);
                                            }}
                                        />
                                        <input
                                            type="range"
                                            className="range-input range-input-max"
                                            min="140"
                                            max="220"
                                            value={maxHeight}
                                            onChange={(e) => {
                                                const val = parseInt(e.target.value);
                                                if (val >= minHeight) setMaxHeight(val);
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="filter-group">
                                    <label className="filter-label">
                                        Weight: {minWeight} - {maxWeight} kg
                                    </label>
                                    <div className="range-slider">
                                        <input
                                            type="range"
                                            className="range-input range-input-min"
                                            min="40"
                                            max="120"
                                            value={minWeight}
                                            onChange={(e) => {
                                                const val = parseInt(e.target.value);
                                                if (val <= maxWeight) setMinWeight(val);
                                            }}
                                        />
                                        <input
                                            type="range"
                                            className="range-input range-input-max"
                                            min="40"
                                            max="120"
                                            value={maxWeight}
                                            onChange={(e) => {
                                                const val = parseInt(e.target.value);
                                                if (val >= minWeight) setMaxWeight(val);
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </aside>

                        <section className="explore-main">
                            <div className="explore-section-header">
                                <div>
                                    <h2>{userRole === "player" ? "People you may know" : "Players Database"}</h2>
                                    <p>{userRole === "player" ? "Curated from your filters and search" : "Search and favorite players to build your database"}</p>
                                </div>
                                <div className="explore-section-right">
                                    <div className="explore-section-meta">
                                        {players.length} results
                                    </div>
                                    <div className="explore-view-toggle">
                                        <button
                                            className={`explore-view-btn ${viewMode === "grid" ? "active" : ""}`}
                                            onClick={() => setViewMode("grid")}
                                            title="Grid View"
                                        >
                                            ▦
                                        </button>
                                        <button
                                            className={`explore-view-btn ${viewMode === "list" ? "active" : ""}`}
                                            onClick={() => setViewMode("list")}
                                            title="List View"
                                        >
                                            ≡
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {totalPages > 1 && (
                                <div className="pagination-controls">
                                    <button
                                        className="pagination-btn"
                                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                    >
                                        ← Previous
                                    </button>
                                    <span className="pagination-info">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        className="pagination-btn"
                                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                    >
                                        Next →
                                    </button>
                                </div>
                            )}

                            {loading && (
                                <div className={viewMode === "grid" ? "explore-players-grid" : "explore-players-list"}>
                                    {[1, 2, 3, 4, 5, 6].map((index) => (
                                        <div key={index} className="explore-skeleton-card">
                                            <div className="explore-skeleton-banner" />
                                            <div className="explore-skeleton-body">
                                                <div className="explore-skeleton-avatar" />
                                                <div className="explore-skeleton-info">
                                                    <div className="explore-skeleton-line explore-skeleton-line--name" />
                                                    <div className="explore-skeleton-line explore-skeleton-line--meta" />
                                                    <div className="explore-skeleton-line explore-skeleton-line--tags" />
                                                </div>
                                            </div>
                                            <div className="explore-skeleton-actions">
                                                <div className="explore-skeleton-line explore-skeleton-line--action" />
                                                <div className="explore-skeleton-line explore-skeleton-line--action" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {error && (
                                <div className="error-banner">
                                    <p>{error}</p>
                                </div>
                            )}

                            {!loading && !error && players.length === 0 && (
                                <div className="results-placeholder">
                                    <p>No players found matching your criteria.</p>
                                </div>
                            )}

                            <div className={viewMode === "grid" ? "explore-players-grid" : "explore-players-list"}>
                                {paginatedPlayers.map((player) => {
                                    const age = getAge(player.DateOfBirth);
                                    const fullName = `${player.firstname || ""} ${player.lastname || ""}`.trim();
                                    const relationship = player.user_id ? connectionsByUserId[player.user_id] : null;
                                    const status = relationship?.status || null;
                                    const isRequester = relationship?.requester_id === authedUser?.id;
                                    const isAddressee = relationship?.addressee_id === authedUser?.id;
                                    const isLoadingAction = connectionActionId === player.user_id;
                                    const isFollowing = status === "accepted" || !!followsByUserId[player.user_id];
                                    const isFollowLoading = followActionId === player.user_id;

                                    return (
                                        <div key={player.id} className="explore-person-card">
                                            <div className="explore-person-banner" />
                                            <div className="explore-person-body">
                                                    {player.avatar_url
                                                        ? <img src={player.avatar_url} alt={fullName} className="explore-person-avatar avatar-img" />
                                                        : <div className="explore-person-avatar">{(player.firstname?.charAt(0) || "P").toUpperCase()}</div>
                                                    }
                                                <div className="explore-person-info">
                                                    <Link to={`/player/${player.user_id}`} className="explore-person-name">
                                                        {fullName}
                                                        {player.verified && <span className="explore-verified-badge">✓</span>}
                                                    </Link>
                                                    <div className="explore-person-meta">
                                                        {age && <span>{age} yrs</span>}
                                                        {player.city && <span>• {player.city}</span>}
                                                        {player.footedness && <span>• {player.footedness} foot</span>}
                                                    </div>
                                                    {player.positions && player.positions.length > 0 && (
                                                        <div className="explore-person-tags">
                                                            {player.positions.map((pos) => (
                                                                <span key={pos} className="explore-tag">
                                                                    {pos}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            {(userRole === "scout" || userRole === "coach") && highlightsByPlayerId[player.id] && highlightsByPlayerId[player.id].length > 0 && (
                                                <div className="explore-highlight-preview">
                                                    {highlightsByPlayerId[player.id][0].media_url && (
                                                        <button
                                                            className="explore-highlight-thumbnail"
                                                            onClick={() => setSelectedHighlight({
                                                                url: highlightsByPlayerId[player.id][0].media_url,
                                                                type: highlightsByPlayerId[player.id][0].media_type || 'video',
                                                                playerName: fullName
                                                            })}
                                                            title="View highlight"
                                                        >
                                                            {highlightsByPlayerId[player.id][0].media_type === 'video' ? (
                                                                <video src={highlightsByPlayerId[player.id][0].media_url} />
                                                            ) : (
                                                                <img src={highlightsByPlayerId[player.id][0].media_url} alt="Player highlight" />
                                                            )}
                                                            <div className="explore-highlight-overlay">
                                                                <div className="explore-highlight-play">▶</div>
                                                            </div>
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            <div className="explore-person-actions">
                                                <Link to={`/player/${player.user_id}`} className="explore-view-link">
                                                    View profile
                                                </Link>

                                                {authedUser && player.user_id && authedUser.id !== player.user_id && (
                                                    <div className="explore-connect-actions">
                                                        <button
                                                            className={`explore-btn ${isFollowing ? "explore-btn-following" : "explore-btn-outline"}`}
                                                            onClick={async () => {
                                                                if (status === "accepted") return;
                                                                setFollowActionId(player.user_id);
                                                                try {
                                                                    if (isFollowing) {
                                                                        await unfollowUser(player.user_id);
                                                                        setFollowsByUserId((prev) => ({ ...prev, [player.user_id]: false }));
                                                                    } else {
                                                                        await followUser(player.user_id);
                                                                        setFollowsByUserId((prev) => ({ ...prev, [player.user_id]: true }));
                                                                    }
                                                                } catch (err) {
                                                                    console.error("Error toggling follow:", err);
                                                                } finally {
                                                                    setFollowActionId(null);
                                                                }
                                                            }}
                                                            disabled={isFollowLoading || status === "accepted"}
                                                        >
                                                            {isFollowLoading
                                                                ? "Working..."
                                                                : status === "accepted"
                                                                    ? "Following"
                                                                    : isFollowing
                                                                        ? "Following"
                                                                        : "Follow"}
                                                        </button>

                                                        {userRole === "scout" || userRole === "coach" ? (
                                                            // Non-player mode: show "Add to Favorites" button
                                                            <button
                                                                className={`explore-btn ${favoritedByPlayerId[player.id] ? "explore-btn-favorited" : "explore-btn-scout"}`}
                                                                onClick={async () => {
                                                                    try {
                                                                        if (favoritedByPlayerId[player.id]) {
                                                                            await removeFavorite(authedUser.id, player.id);
                                                                            setFavoritedByPlayerId({ ...favoritedByPlayerId, [player.id]: false });
                                                                        } else {
                                                                            await addFavorite(authedUser.id, player.id);
                                                                            setFavoritedByPlayerId({ ...favoritedByPlayerId, [player.id]: true });
                                                                        }
                                                                    } catch (err) {
                                                                        console.error("Error toggling favorite:", err);
                                                                    }
                                                                }}
                                                            >
                                                                {favoritedByPlayerId[player.id] ? "★ Favorited" : "☆ Add to Favorites"}
                                                            </button>
                                                        ) : userRole === "player" ? (
                                                            // Player mode: show connection buttons
                                                            <>
                                                                {status === "pending" && isAddressee ? (
                                                                    <>
                                                                        <button
                                                                            className="explore-btn explore-btn-primary"
                                                                            onClick={async () => {
                                                                                setConnectionActionId(player.user_id);
                                                                                try {
                                                                                    await acceptConnectionRequest(relationship.id);
                                                                                    await refreshConnections(authedUser);
                                                                                } catch (err) {
                                                                                    console.error("Error accepting request:", err);
                                                                                } finally {
                                                                                    setConnectionActionId(null);
                                                                                }
                                                                            }}
                                                                            disabled={isLoadingAction}
                                                                        >
                                                                            {isLoadingAction ? "Accepting..." : "Accept"}
                                                                        </button>
                                                                        <button
                                                                            className="explore-btn explore-btn-outline"
                                                                            onClick={async () => {
                                                                                setConnectionActionId(player.user_id);
                                                                                try {
                                                                                    await rejectConnectionRequest(relationship.id);
                                                                                    await refreshConnections(authedUser);
                                                                                } catch (err) {
                                                                                    console.error("Error declining request:", err);
                                                                                } finally {
                                                                                    setConnectionActionId(null);
                                                                                }
                                                                            }}
                                                                            disabled={isLoadingAction}
                                                                        >
                                                                            Decline
                                                                        </button>
                                                                    </>
                                                                ) : (
                                                                    <button
                                                                        className={`explore-btn ${
                                                                            status === "accepted"
                                                                                ? "explore-btn-success"
                                                                                : status === "pending" && isRequester
                                                                                    ? "explore-btn-warning"
                                                                                    : "explore-btn-primary"
                                                                        }`}
                                                                        onClick={async () => {
                                                                            if (status === "accepted" || status === "pending") {
                                                                                return;
                                                                            }
                                                                            setConnectionActionId(player.user_id);
                                                                            try {
                                                                                await sendConnectionRequest(player.user_id);
                                                                                await refreshConnections(authedUser);
                                                                            } catch (err) {
                                                                                console.error("Error sending request:", err);
                                                                            } finally {
                                                                                setConnectionActionId(null);
                                                                            }
                                                                        }}
                                                                        disabled={isLoadingAction || status === "accepted" || status === "pending"}
                                                                    >
                                                                        {isLoadingAction
                                                                            ? "Sending..."
                                                                            : status === "accepted"
                                                                                ? "Connected"
                                                                                : status === "pending" && isRequester
                                                                                    ? "Pending"
                                                                                    : "Connect"}
                                                                    </button>
                                                                )}
                                                            </>
                                                        ) : null}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    </div>
                </div>

                {selectedHighlight && (
                    <div className="explore-highlight-modal-overlay" onClick={() => setSelectedHighlight(null)}>
                        <div className="explore-highlight-modal" onClick={(e) => e.stopPropagation()}>
                            <button
                                className="explore-highlight-close"
                                onClick={() => setSelectedHighlight(null)}
                            >
                                ✕
                            </button>
                            <div className="explore-highlight-modal-content">
                                {selectedHighlight.type === 'video' ? (
                                    <video src={selectedHighlight.url} controls autoPlay />
                                ) : (
                                    <img src={selectedHighlight.url} alt="Highlight" />
                                )}
                            </div>
                            <div className="explore-highlight-modal-info">
                                <p>{selectedHighlight.playerName}</p>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        );
}

export default Explore;
